import { api_json, ApiError } from './api'
import { icon } from './icons'

type PickerMode = 'single' | 'multi'

type PickerItem = {
  id: number
  module_code: string
  title: string
  subtitle: string
  search_text: string
  kind_text: string
}

type PickerContext = {
  field_name: string
  mode: PickerMode
  source_module_field: string | null
  form: HTMLFormElement
  hidden_input: HTMLInputElement
  preview_input: HTMLInputElement
  pick_button: HTMLButtonElement
  clear_button: HTMLButtonElement
  helper_text: HTMLParagraphElement
  chips_box: HTMLDivElement
  selected_ids: number[]
  selected_labels: Map<number, string>
  last_module_code: string | null
}

const PICKER_MODAL_ID = 'nr01-entity-picker-modal'
const MODULE_CACHE = new Map<string, PickerItem[]>()
const ROOT_OBSERVERS = new WeakMap<ParentNode, MutationObserver>()

const M01_KIND_HINTS: Record<string, string[]> = {
  unidade_id: ['unidade'],
  setor_id: ['setor'],
  local_setor_id: ['setor', 'local'],
  funcao_id: ['funcao'],
  ghe_id: ['ghe'],
  colaborador_id: ['colaborador', 'trabalhador', 'funcionario', 'usuario']
}

const STATIC_FIELD_MODULE_MAP: Record<string, string> = {
  unidade_id: 'M01',
  setor_id: 'M01',
  local_setor_id: 'M01',
  funcao_id: 'M01',
  ghe_id: 'M01',
  colaborador_id: 'M01'
}

function as_record(value: unknown): Record<string, unknown> {
  if (value && typeof value === 'object') return value as Record<string, unknown>
  return {}
}

function to_positive_int(value: unknown): number | null {
  const parsed = Number(String(value ?? '').trim())
  if (!Number.isInteger(parsed) || parsed <= 0 || parsed > Number.MAX_SAFE_INTEGER) return null
  return parsed
}

function escape_html(value: unknown): string {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}

function normalize_text(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
}

function normalize_module_code(value: unknown): string | null {
  const raw = String(value ?? '').trim().toUpperCase()
  if (!raw) return null

  const exact = /^M(\d{2})$/.exec(raw)
  if (exact) return `M${exact[1]}`

  const flexible = /^M?(\d{1,2})$/.exec(raw)
  if (!flexible) return null

  const numeric = Number(flexible[1])
  if (!Number.isInteger(numeric) || numeric < 1 || numeric > 99) return null
  return `M${String(numeric).padStart(2, '0')}`
}

function module_slug(module_code: string): string {
  return module_code.toLowerCase()
}

function first_text(record: Record<string, unknown>, keys: readonly string[]): string | null {
  for (const key of keys) {
    const text = String(record[key] ?? '').trim()
    if (text) return text
  }
  return null
}

function parse_item_list(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload
  const record = as_record(payload)

  const direct_keys = [
    'items',
    'registros',
    'dados',
    'results',
    'terceiros',
    'requisitos',
    'cursos',
    'atas',
    'indicadores',
    'documentos'
  ]

  for (const key of direct_keys) {
    const candidate = record[key]
    if (Array.isArray(candidate)) return candidate
  }

  return []
}

function map_picker_item(module_code: string, value: unknown): PickerItem | null {
  const row = as_record(value)
  const extra = as_record(row.dados_adicionais ?? row.payload_json)
  const id = to_positive_int(row.id)
  if (!id) return null

  const title = first_text(row, [
    'titulo',
    'nome',
    'codigo',
    'codigo_interno',
    'razao_social',
    'atividade',
    'norma',
    'tipo',
    'numero_os',
    'numero_inspecao'
  ]) || first_text(extra, [
    'titulo',
    'nome',
    'codigo',
    'codigo_interno',
    'razao_social',
    'atividade',
    'norma',
    'tipo',
    'tipo_cadastro',
    'numero_os',
    'numero_inspecao'
  ]) || 'Registro sem título'

  const status = first_text(row, ['status', 'status_atendimento', 'status_conformidade'])
    || first_text(extra, ['status', 'status_atendimento', 'status_conformidade'])
  const due = first_text(row, ['data_evento', 'due_date', 'data_prevista'])
    || first_text(extra, ['data_evento', 'due_date', 'data_prevista', 'vencimento_principal'])

  const subtitle_parts: string[] = []
  if (status) subtitle_parts.push(status.replaceAll('_', ' '))
  if (due) subtitle_parts.push(due)

  const kind = first_text(extra, ['tipo_cadastro', 'tipo']) || first_text(row, ['tipo_cadastro', 'tipo']) || ''
  const search_blob = [
    String(id),
    title,
    subtitle_parts.join(' '),
    kind,
    first_text(extra, ['codigo_interno', 'codigo', 'numero_os', 'numero_inspecao']) || ''
  ].join(' ')

  return {
    id,
    module_code,
    title,
    subtitle: subtitle_parts.join(' · '),
    search_text: normalize_text(search_blob),
    kind_text: normalize_text(kind)
  }
}

async function load_module_items(module_code: string): Promise<PickerItem[]> {
  const cached = MODULE_CACHE.get(module_code)
  if (cached) return cached

  const payload = await api_json<unknown>(`/api/modules/${module_slug(module_code)}/items?limit=250`)
  const items = parse_item_list(payload)
    .map((entry) => map_picker_item(module_code, entry))
    .filter((entry): entry is PickerItem => entry !== null)

  MODULE_CACHE.set(module_code, items)
  return items
}

function parse_input_ids(value: string, mode: PickerMode): number[] {
  const parts = value
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean)

  const parsed = parts
    .map((entry) => to_positive_int(entry))
    .filter((entry): entry is number => entry !== null)

  const unique = Array.from(new Set(parsed))
  if (mode === 'single') return unique.slice(0, 1)
  return unique
}

function get_form_field_value(form: HTMLFormElement, field_name: string): string {
  const field = form.elements.namedItem(field_name)
  if (!field) return ''

  if (field instanceof RadioNodeList) return String(field.value ?? '').trim()
  if (field instanceof HTMLInputElement || field instanceof HTMLSelectElement || field instanceof HTMLTextAreaElement) {
    return String(field.value ?? '').trim()
  }

  return ''
}

function resolve_source_module_field(field_name: string): string | null {
  if (field_name === 'source_entity_id') return 'source_module'
  if (field_name === 'origem_entidade_id') return 'origem_modulo'
  return null
}

function resolve_field_module(field_name: string, form: HTMLFormElement): string | null {
  const source_module_field = resolve_source_module_field(field_name)
  if (source_module_field) return normalize_module_code(get_form_field_value(form, source_module_field))

  const normalized = field_name.trim().toLowerCase()
  const prefixed = /^m(\d{2})(?:_|$)/.exec(normalized)
  if (prefixed) return `M${prefixed[1]}`

  return STATIC_FIELD_MODULE_MAP[normalized] || null
}

function format_selection_label(item: PickerItem): string {
  return `ID #${item.id} · ${item.title}`
}

function render_chips(context: PickerContext): void {
  context.chips_box.innerHTML = ''
  if (context.mode !== 'multi' || !context.selected_ids.length) {
    context.chips_box.classList.add('hidden')
    return
  }

  context.chips_box.classList.remove('hidden')
  context.selected_ids.forEach((id) => {
    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'inline-flex items-center gap-1 rounded-sm border border-border bg-surface px-2.5 py-1 text-xs text-ink hover:bg-surface2'
    button.setAttribute('data-entity-picker-remove-id', String(id))
    button.innerHTML = `
      <span>${escape_html(context.selected_labels.get(id) || `ID #${id}`)}</span>
      <span aria-hidden="true">×</span>
    `
    context.chips_box.appendChild(button)
  })
}

function sync_hidden_value(context: PickerContext): void {
  const value = context.mode === 'single'
    ? String(context.selected_ids[0] ?? '')
    : context.selected_ids.join(', ')
  context.hidden_input.value = value
}

function sync_preview(context: PickerContext): void {
  if (!context.selected_ids.length) {
    context.preview_input.value = ''
    context.preview_input.placeholder = context.mode === 'single'
      ? 'Nenhuma referência selecionada'
      : 'Nenhuma referência selecionada'
    context.clear_button.hidden = true
    render_chips(context)
    return
  }

  if (context.mode === 'single') {
    const id = context.selected_ids[0]
    context.preview_input.value = context.selected_labels.get(id) || `ID #${id}`
  } else {
    context.preview_input.value = `${context.selected_ids.length} referência(s) selecionada(s)`
  }

  context.clear_button.hidden = false
  render_chips(context)
}

function set_helper_message(context: PickerContext, message: string, as_error = false): void {
  context.helper_text.textContent = message
  context.helper_text.classList.toggle('text-danger', as_error)
  context.helper_text.classList.toggle('text-muted', !as_error)
}

function default_helper_text(context: PickerContext): string {
  if (context.source_module_field) {
    const module_code = resolve_field_module(context.field_name, context.form)
    if (module_code) {
      return `Selecione ou pesquise um registro existente no módulo ${module_code}.`
    }
    return 'Defina o módulo de origem para habilitar a seleção.'
  }
  return 'Selecione ou pesquise um registro existente.'
}

function apply_module_filter(context: PickerContext, module_code: string, items: PickerItem[]): PickerItem[] {
  if (module_code !== 'M01') return items

  const hints = M01_KIND_HINTS[context.field_name]
  if (!hints?.length) return items

  const normalized_hints = hints.map((entry) => normalize_text(entry))
  const filtered = items.filter((item) => {
    const haystack = `${item.kind_text} ${normalize_text(item.title)}`
    return normalized_hints.some((entry) => haystack.includes(entry))
  })

  return filtered.length ? filtered : items
}

function remove_picker_modal(): void {
  document.getElementById(PICKER_MODAL_ID)?.remove()
}

function render_modal_rows(items: PickerItem[]): string {
  if (!items.length) {
    return '<div class="rounded-sm border border-border bg-surface2 px-4 py-6 text-sm text-muted">Nenhum registro encontrado para o filtro informado.</div>'
  }

  return items.map((item) => `
    <button
      class="w-full rounded-sm border border-border bg-surface px-3 py-3 text-left hover:bg-surface2"
      data-entity-picker-option-id="${item.id}"
      type="button"
    >
      <div class="text-[11px] font-semibold uppercase tracking-wide text-muted">ID #${item.id}</div>
      <div class="mt-0.5 text-sm font-medium text-ink">${escape_html(item.title)}</div>
      ${item.subtitle ? `<div class="mt-1 text-xs text-muted">${escape_html(item.subtitle)}</div>` : ''}
    </button>
  `).join('\n')
}

function open_picker_modal(context: PickerContext, module_code: string, items: PickerItem[]): void {
  remove_picker_modal()

  const filtered = apply_module_filter(context, module_code, items)
  const item_map = new Map(filtered.map((item) => [item.id, item]))

  const modal = document.createElement('div')
  modal.id = PICKER_MODAL_ID
  modal.className = 'fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4'
  modal.innerHTML = `
    <div class="w-full max-w-3xl rounded-sm border border-border bg-surface">
      <div class="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
        <div>
          <h3 class="text-sm font-semibold text-ink">Selecionar referência (${module_code})</h3>
          <p class="text-xs text-muted">Escolha um registro existente para preencher o campo.</p>
        </div>
        <button class="inline-flex h-10 items-center gap-2 rounded-sm border border-border bg-surface2 px-3 text-sm text-ink hover:bg-surface" id="entity-picker-close" type="button">
          ${icon('close')}
          Fechar
        </button>
      </div>

      <div class="px-4 py-3">
        <label class="grid gap-1">
          <span class="text-xs font-semibold uppercase tracking-wide text-muted">Buscar registro</span>
          <input class="h-11 rounded-sm border border-border bg-surface2 px-4 text-sm text-ink outline-none focus:border-primary/60" id="entity-picker-search" placeholder="Buscar por ID, título ou código..." />
        </label>
      </div>

      <div class="max-h-[60vh] overflow-auto px-4 pb-4" id="entity-picker-list">
        <div class="grid gap-2">
          ${render_modal_rows(filtered)}
        </div>
      </div>
    </div>
  `
  document.body.appendChild(modal)

  const close = () => {
    modal.remove()
    document.removeEventListener('keydown', on_keydown)
  }

  const on_keydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') close()
  }
  document.addEventListener('keydown', on_keydown)

  const list = modal.querySelector('#entity-picker-list') as HTMLElement | null
  const search = modal.querySelector('#entity-picker-search') as HTMLInputElement | null

  const render = (query: string) => {
    if (!list) return
    const normalized_query = normalize_text(query)
    const scoped = normalized_query
      ? filtered.filter((item) => item.search_text.includes(normalized_query))
      : filtered

    list.innerHTML = `<div class="grid gap-2">${render_modal_rows(scoped)}</div>`
  }

  search?.addEventListener('input', () => render(search.value))
  search?.focus()

  modal.querySelector('#entity-picker-close')?.addEventListener('click', close)
  modal.addEventListener('click', (event) => {
    if (event.target === modal) close()
  })

  list?.addEventListener('click', (event) => {
    const target = (event.target as HTMLElement).closest('[data-entity-picker-option-id]') as HTMLElement | null
    if (!target) return

    const id = to_positive_int(target.getAttribute('data-entity-picker-option-id'))
    if (!id) return

    const item = item_map.get(id)
    if (!item) return

    if (context.mode === 'single') {
      context.selected_ids = [id]
    } else if (!context.selected_ids.includes(id)) {
      context.selected_ids = [...context.selected_ids, id]
    }

    context.selected_labels.set(id, format_selection_label(item))
    sync_hidden_value(context)
    sync_preview(context)
    set_helper_message(context, default_helper_text(context), false)
    close()
  })
}

function resolve_error_message(error: unknown, module_code: string): string {
  if (error instanceof ApiError) {
    if (error.status === 401 || error.status === 403) {
      return `Você não tem permissão para listar registros do ${module_code}.`
    }
    if (error.status === 404) {
      return `A API do ${module_code} não está disponível nesta instância.`
    }
    return `Falha ao carregar registros do ${module_code} (HTTP ${error.status}).`
  }

  if (error instanceof Error && error.message.trim()) return error.message
  return `Falha ao carregar registros do ${module_code}.`
}

async function open_picker(context: PickerContext): Promise<void> {
  const module_code = resolve_field_module(context.field_name, context.form)
  if (!module_code) {
    set_helper_message(context, 'Defina o módulo de origem antes de selecionar o registro.', true)
    return
  }

  try {
    set_helper_message(context, `Carregando registros de ${module_code}...`)
    const items = await load_module_items(module_code)
    set_helper_message(context, default_helper_text(context))
    open_picker_modal(context, module_code, items)
  } catch (error) {
    set_helper_message(context, resolve_error_message(error, module_code), true)
  }
}

function should_enhance_input(input: HTMLInputElement): boolean {
  const name = String(input.name || '').trim().toLowerCase()
  if (!name) return false
  if (input.dataset.entityPickerEnhanced === '1') return false
  if (input.type === 'hidden') return false
  if (input.type === 'checkbox' || input.type === 'radio' || input.type === 'date' || input.type === 'file') return false
  if (name.endsWith('public_id')) return false
  if (!/_ids?$/.test(name)) return false
  return true
}

function build_picker_context(input: HTMLInputElement): PickerContext | null {
  const form = input.form
  if (!form) return null

  const field_name = String(input.name || '').trim()
  const source_module_field = resolve_source_module_field(field_name)
  if (!resolve_field_module(field_name, form) && !source_module_field) return null

  const mode: PickerMode = field_name.endsWith('_ids') ? 'multi' : 'single'
  const selected_ids = parse_input_ids(String(input.value ?? ''), mode)

  input.dataset.entityPickerEnhanced = '1'
  input.type = 'hidden'
  input.classList.add('hidden')

  const panel = document.createElement('div')
  panel.className = 'mt-1 space-y-2'

  const controls = document.createElement('div')
  controls.className = 'flex flex-wrap items-center gap-2 normal-case tracking-normal'

  const preview_input = document.createElement('input')
  preview_input.type = 'text'
  preview_input.readOnly = true
  preview_input.className = 'h-11 min-w-0 flex-1 rounded-sm border border-border bg-surface2 px-3 text-sm text-ink normal-case tracking-normal outline-none'
  preview_input.placeholder = 'Nenhuma referência selecionada'

  const pick_button = document.createElement('button')
  pick_button.type = 'button'
  pick_button.className = 'inline-flex h-11 items-center gap-2 rounded-sm border border-border bg-surface2 px-3 text-sm font-medium text-ink normal-case tracking-normal hover:bg-surface'
  pick_button.innerHTML = `${icon('modules')} Selecionar`

  const clear_button = document.createElement('button')
  clear_button.type = 'button'
  clear_button.className = 'inline-flex h-11 items-center gap-2 rounded-sm border border-border bg-surface2 px-3 text-sm font-medium text-ink normal-case tracking-normal hover:bg-surface'
  clear_button.innerHTML = `${icon('close')} Limpar`
  clear_button.hidden = true

  controls.appendChild(preview_input)
  controls.appendChild(pick_button)
  controls.appendChild(clear_button)

  const chips_box = document.createElement('div')
  chips_box.className = 'hidden flex flex-wrap gap-2 normal-case tracking-normal'

  const helper_text = document.createElement('p')
  helper_text.className = 'text-xs text-muted normal-case tracking-normal'

  panel.appendChild(controls)
  panel.appendChild(chips_box)
  panel.appendChild(helper_text)
  input.insertAdjacentElement('afterend', panel)

  const context: PickerContext = {
    field_name,
    mode,
    source_module_field,
    form,
    hidden_input: input,
    preview_input,
    pick_button,
    clear_button,
    helper_text,
    chips_box,
    selected_ids,
    selected_labels: new Map<number, string>(),
    last_module_code: resolve_field_module(field_name, form)
  }

  sync_hidden_value(context)
  sync_preview(context)
  set_helper_message(context, default_helper_text(context))

  pick_button.addEventListener('click', () => {
    void open_picker(context)
  })

  clear_button.addEventListener('click', () => {
    context.selected_ids = []
    context.selected_labels.clear()
    sync_hidden_value(context)
    sync_preview(context)
    set_helper_message(context, default_helper_text(context))
  })

  chips_box.addEventListener('click', (event) => {
    const button = (event.target as HTMLElement).closest('[data-entity-picker-remove-id]') as HTMLElement | null
    if (!button) return
    const id = to_positive_int(button.getAttribute('data-entity-picker-remove-id'))
    if (!id) return

    context.selected_ids = context.selected_ids.filter((entry) => entry !== id)
    context.selected_labels.delete(id)
    sync_hidden_value(context)
    sync_preview(context)
    set_helper_message(context, default_helper_text(context))
  })

  if (source_module_field) {
    const source_field = form.elements.namedItem(source_module_field)
    const on_source_change = () => {
      const next_module = resolve_field_module(field_name, form)
      if (next_module !== context.last_module_code) {
        context.selected_ids = []
        context.selected_labels.clear()
        sync_hidden_value(context)
        sync_preview(context)
      }
      context.last_module_code = next_module
      set_helper_message(context, default_helper_text(context))
    }

    if (source_field instanceof RadioNodeList) {
      Array.from(source_field).forEach((entry) => {
        if (entry instanceof HTMLElement) {
          entry.addEventListener('change', on_source_change)
          entry.addEventListener('input', on_source_change)
        }
      })
    } else if (source_field instanceof HTMLInputElement || source_field instanceof HTMLSelectElement || source_field instanceof HTMLTextAreaElement) {
      source_field.addEventListener('change', on_source_change)
      source_field.addEventListener('input', on_source_change)
    }
  }

  form.addEventListener('reset', () => {
    window.setTimeout(() => {
      context.selected_ids = parse_input_ids(String(context.hidden_input.value ?? ''), context.mode)
      context.selected_labels.clear()
      sync_hidden_value(context)
      sync_preview(context)
      set_helper_message(context, default_helper_text(context))
    }, 0)
  })

  return context
}

export function enhance_entity_reference_inputs(root: ParentNode): void {
  remove_picker_modal()

  const inputs = Array.from(root.querySelectorAll('input[name]'))
  inputs.forEach((entry) => {
    if (!(entry instanceof HTMLInputElement)) return
    if (!should_enhance_input(entry)) return
    build_picker_context(entry)
  })
}

export function watch_entity_reference_inputs(root: ParentNode): () => void {
  enhance_entity_reference_inputs(root)

  const current = ROOT_OBSERVERS.get(root)
  if (current) current.disconnect()

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const added_node of Array.from(mutation.addedNodes)) {
        if (!(added_node instanceof Element)) continue
        enhance_entity_reference_inputs(added_node)
      }
    }
  })

  if (root instanceof Element || root instanceof Document) {
    observer.observe(root, { childList: true, subtree: true })
  }
  ROOT_OBSERVERS.set(root, observer)

  return () => {
    observer.disconnect()
    if (ROOT_OBSERVERS.get(root) === observer) ROOT_OBSERVERS.delete(root)
  }
}
